import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { neutral } from '_lex/_foundations/phxColors';

export interface ToCItemData {
  id: string;
  title: string;
  type: 'manual' | 'preface' | 'chapter' | 'policy';
  hasChildren: boolean;
  children?: ToCItemData[];
}

interface ToCItemProps {
  item: ToCItemData;
  isSelected: boolean;
  isIndeterminate?: boolean;
  isExpanded?: boolean;
  onToggle: (id: string) => void;
  onExpand?: (itemId: string) => void;
  depth?: number;
  disabled?: boolean;
}

const ToCItem: React.FC<ToCItemProps> = ({
  item,
  isSelected,
  isIndeterminate = false,
  isExpanded = false,
  onToggle,
  onExpand,
  depth = 0,
  disabled = false,
}) => {
  const handleCheckboxChange = () => {
    onToggle(item.id);
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Only expand if clicking outside the checkbox
    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
      return;
    }
    if (onExpand && item.hasChildren) {
      onExpand(item.id);
    }
  };

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1,
        px: 2,
        pl: 2 + depth * 3,
        borderBottom: `1px solid ${neutral[200]}`,
        height: 80,
        cursor: item.hasChildren ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <Checkbox
        checked={isSelected}
        indeterminate={isIndeterminate}
        onChange={handleCheckboxChange}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        sx={{ mr: 1 }}
      />
      <Typography
        variant='body2'
        sx={{
          flex: 1,
          fontWeight: item.hasChildren ? 600 : 400,
        }}
      >
        {item.title}
      </Typography>
      {item.hasChildren && (
        <Box sx={{ color: neutral[600], display: 'flex', alignItems: 'center' }}>
          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </Box>
      )}
    </Box>
  );
};

export default ToCItem;